import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "../../lib/utils";

/**
 * shadcn/ui Dialog — the standard component, ported to this codebase.
 *
 * A thin, pre-styled wrapper over @radix-ui/react-dialog (the same engine the
 * shadcn CLI would install). Differences from upstream are only cosmetic to fit
 * this repo: JSX instead of TSX, relative imports instead of the `@/` alias,
 * heroicons instead of lucide, and the project's Tailwind tokens
 * (black-150 / orange-150 / gray-*). Radix handles the focus trap,
 * Escape-to-close, scroll-lock, focus restoration, outside-click, portal
 * rendering and aria wiring — so consumers don't reimplement any of that.
 */

const Dialog = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef(function DialogOverlay(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-[1000] bg-black-150/40 backdrop-blur-sm",
        className,
      )}
      {...props}
    />
  );
});

const DialogContent = React.forwardRef(function DialogContent(
  { className, children, showClose = true, maxWidth = "max-w-md", ...props },
  ref,
) {
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Height-capped flex column: the header + footer stay pinned (shrink-0)
          // while a DialogBody scrolls in the middle. `overflow-hidden` keeps the
          // rounded corners clipping cleanly; dvh accounts for mobile browser chrome.
          "fixed left-1/2 top-1/2 z-[1000] flex max-h-[90dvh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl bg-white p-5 shadow-xl focus:outline-none sm:p-6",
          maxWidth,
          className,
        )}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            aria-label="Close"
            className="absolute right-4 top-4 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
          >
            <XMarkIcon className="h-6 w-6" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

const DialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex shrink-0 flex-col gap-1.5 pr-8", className)}
    {...props}
  />
);

// Scrollable middle region. Header/footer stay pinned; only this scrolls when
// content exceeds the dialog's max height (e.g. tall modals on an iPhone SE).
const DialogBody = ({ className, ...props }) => (
  <div
    className={cn("min-h-0 flex-1 overflow-y-auto", className)}
    {...props}
  />
);

const DialogFooter = ({ className, ...props }) => (
  <div
    className={cn(
      "flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);

const DialogSeparator = ({ className, ...props }) => (
  <div
    className={cn("shrink-0 border-t border-gray-275", className)}
    {...props}
  />
);

const DialogTitle = React.forwardRef(function DialogTitle(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(
        "mb-0 text-xl font-bold text-black-150 sm:text-2xl",
        className,
      )}
      {...props}
    />
  );
});

const DialogDescription = React.forwardRef(function DialogDescription(
  { className, ...props },
  ref,
) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("mb-0 text-base leading-relaxed text-gray-150", className)}
      {...props}
    />
  );
});

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogSeparator,
  DialogTitle,
  DialogDescription,
};
